class ProductPatternMatches extends HTMLElement {
  constructor() {
    super();
    this.patterns = [];
    this.matches = [];
    this.currentPage = 1;
    this.patternsPerPage = 4;
  }

  async connectedCallback() {
    this.cacheElements();
    this.patternsPerPage = Math.max(1, Number(this.dataset.patternsPerPage || 4));

    if (!this.productHandle && !this.productTitle) return;

    this.patterns = await this.readPatterns();
    this.matches = this.findMatches();

    if (!this.matches.length) return;

    this.hidden = false;
    this.render();
  }

  cacheElements() {
    this.grid = this.querySelector('[data-product-pattern-grid]');
    this.pagination = this.querySelector('[data-product-pattern-pagination]');
    this.status = this.querySelector('[data-product-pattern-status]');
    this.productHandle = this.normaliseHandle(this.dataset.productHandle);
    this.productTitle = this.normaliseText(this.dataset.productTitle);
    this.productTitleRaw = this.dataset.productTitle || 'det här garnet';
    this.patternPageUrl = this.dataset.patternPageUrl || '/pages/monster';

    this.pagination?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-product-pattern-page]');
      if (!button || button.disabled) return;
      event.preventDefault();
      this.goToPage(Number(button.dataset.productPatternPage || 1));
    });
  }

  async readPatterns() {
    const catalogUrl = this.dataset.patternCatalogUrl;
    if (!catalogUrl) return [];

    if (window.KickisPatternCatalog?.load) {
      try {
        const catalog = await window.KickisPatternCatalog.load(catalogUrl, {
          manifestUrl: this.dataset.patternCatalogManifestUrl || '',
        });
        if (Array.isArray(catalog)) return catalog;
        if (Array.isArray(catalog.patterns)) return catalog.patterns;
      } catch (error) {
        console.warn('Kunde inte läsa mönsterkatalogen via versionskontroll på produktsidan.', error);
      }
    }

    try {
      const response = await fetch(catalogUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) return [];
      const catalog = await response.json();
      if (Array.isArray(catalog)) return catalog;
      if (Array.isArray(catalog.patterns)) return catalog.patterns;
    } catch (error) {
      console.warn('Kunde inte läsa mönsterkatalogen på produktsidan.', error);
    }

    return [];
  }

  findMatches() {
    const seen = new Set();

    return this.patterns
      .map((pattern) => {
        const matchingVariants = this.getPatternVariants(pattern)
          .filter((variant) => this.variantUsesCurrentProduct(pattern, variant));

        const usesTopLevelYarn = !matchingVariants.length &&
          this.getYarns(pattern, null).some((yarn) => this.isCurrentProductYarn(yarn));

        if (!matchingVariants.length && !usesTopLevelYarn) return null;
        if (seen.has(pattern.id)) return null;
        seen.add(pattern.id);

        const matchedVariant = matchingVariants[0] || this.getPatternVariants(pattern)[0] || null;
        return {
          pattern,
          matchedVariant,
          matchingVariantCount: Math.max(matchingVariants.length, usesTopLevelYarn ? 1 : 0),
        };
      })
      .filter(Boolean)
      .sort((a, b) => this.getSortTitle(a.pattern).localeCompare(this.getSortTitle(b.pattern), 'sv'));
  }

  getPatternVariants(pattern) {
    if (Array.isArray(pattern?.variants) && pattern.variants.length) return pattern.variants;
    return [{
      id: pattern?.id,
      label: pattern?.variantLabel || '',
      imageUrl: pattern?.imageUrl,
      imageUrls: pattern?.imageUrls,
      yarns: pattern?.yarns,
      sourceUrl: pattern?.sourceUrl,
    }];
  }

  variantUsesCurrentProduct(pattern, variant) {
    return this.getYarns(pattern, variant).some((yarn) => this.isCurrentProductYarn(yarn));
  }

  getYarns(pattern, variant) {
    const yarns = [];

    if (Array.isArray(pattern?.yarns)) yarns.push(...pattern.yarns);
    if (Array.isArray(variant?.yarns)) yarns.push(...variant.yarns);
    if (Array.isArray(pattern?.yarnNames)) {
      yarns.push(...pattern.yarnNames.map((name) => ({ title: name, storeTitle: name, sourceYarn: name })));
    }

    return yarns;
  }

  isCurrentProductYarn(yarn) {
    const yarnHandles = [
      yarn?.handle,
      yarn?.productHandle,
      yarn?.storeHandle,
      this.handleFromTitle(yarn?.title),
      this.handleFromTitle(yarn?.storeTitle),
      this.handleFromTitle(yarn?.sourceYarn),
    ].map((value) => this.normaliseHandle(value)).filter(Boolean);

    const yarnTitles = [
      yarn?.title,
      yarn?.storeTitle,
      yarn?.sourceYarn,
    ].map((value) => this.normaliseText(value)).filter(Boolean);

    return Boolean(
      (this.productHandle && yarnHandles.includes(this.productHandle)) ||
      (this.productTitle && yarnTitles.includes(this.productTitle))
    );
  }

  render() {
    const total = this.matches.length;
    const pageCount = Math.max(1, Math.ceil(total / this.patternsPerPage));
    this.currentPage = Math.min(Math.max(1, this.currentPage), pageCount);

    const startIndex = (this.currentPage - 1) * this.patternsPerPage;
    const visibleMatches = this.matches.slice(startIndex, startIndex + this.patternsPerPage);

    if (this.status) {
      const first = startIndex + 1;
      const last = Math.min(startIndex + visibleMatches.length, total);
      this.status.textContent = total > this.patternsPerPage
        ? `${first}-${last} av ${total} mönster med ${this.productTitleRaw} visas.`
        : `${total} mönster med ${this.productTitleRaw}.`;
    }

    if (this.grid) {
      this.grid.innerHTML = visibleMatches.map((match) => this.renderCard(match)).join('');
    }

    this.renderPagination(pageCount, total);
  }

  renderCard(match) {
    const pattern = match.pattern;
    const variant = match.matchedVariant;
    const imageUrl = this.getPatternImage(pattern, variant);
    const patternUrl = this.getPatternUrl(pattern, variant);
    const title = pattern.title || 'Mönster';
    const meta = [pattern.recipeId, pattern.type || pattern.category || pattern.target, (pattern.seasons || [])[0]]
      .filter(Boolean)
      .join(' · ');
    const yarnText = this.getMatchedYarnText(pattern, variant);
    const variantText = match.matchingVariantCount > 1
      ? `${match.matchingVariantCount} färgförslag`
      : (variant?.label || '');

    return `
      <a class="product-pattern-card" href="${this.escapeHtml(patternUrl)}">
        <span class="product-pattern-card__media">
          ${imageUrl
            ? `<img src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(title)}" loading="lazy">`
            : '<span class="product-pattern-card__placeholder" aria-hidden="true">M</span>'}
        </span>
        <span class="product-pattern-card__body">
          <span class="product-pattern-card__title">${this.escapeHtml(title)}</span>
          ${meta ? `<span class="product-pattern-card__meta">${this.escapeHtml(meta)}</span>` : ''}
          ${yarnText ? `<span class="product-pattern-card__yarn">${this.escapeHtml(yarnText)}</span>` : ''}
          ${variantText ? `<span class="product-pattern-card__variant">${this.escapeHtml(variantText)}</span>` : ''}
        </span>
      </a>
    `;
  }

  renderPagination(pageCount, total) {
    if (!this.pagination) return;
    if (pageCount <= 1) {
      this.pagination.innerHTML = '';
      return;
    }

    const previousPage = Math.max(1, this.currentPage - 1);
    const nextPage = Math.min(pageCount, this.currentPage + 1);

    this.pagination.innerHTML = `
      <button
        type="button"
        class="button button--secondary product-patterns__pagination-button"
        data-product-pattern-page="${previousPage}"
        ${this.currentPage === 1 ? 'disabled' : ''}
      >
        Föregående 4
      </button>
      <span class="product-patterns__pagination-count">Sida ${this.currentPage} av ${pageCount}</span>
      <button
        type="button"
        class="button product-patterns__pagination-button"
        data-product-pattern-page="${nextPage}"
        ${this.currentPage === pageCount ? 'disabled' : ''}
      >
        Nästa 4
      </button>
    `;
  }

  goToPage(page) {
    this.currentPage = page;
    this.render();
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  getPatternImage(pattern, variant) {
    const variantImages = Array.isArray(variant?.imageUrls) && variant.imageUrls.length
      ? variant.imageUrls
      : [variant?.imageUrl].filter(Boolean);
    const patternImages = Array.isArray(pattern?.imageUrls) && pattern.imageUrls.length
      ? pattern.imageUrls
      : [pattern?.imageUrl].filter(Boolean);
    return (variantImages[0] || patternImages[0] || '').trim();
  }

  getPatternUrl(pattern, variant) {
    const url = new URL(this.patternPageUrl, window.location.origin);
    url.searchParams.set('pattern', pattern.id);
    if (variant?.id) url.searchParams.set('variant', variant.id);
    return `${url.pathname}${url.search}${url.hash}`;
  }

  getMatchedYarnText(pattern, variant) {
    const yarn = this.getYarns(pattern, variant).find((item) => this.isCurrentProductYarn(item));
    const parts = [
      yarn?.role,
      yarn?.matchedVariant || [yarn?.storeColor || yarn?.sourceColor, yarn?.colorCode].filter(Boolean).join(' '),
    ].filter(Boolean);
    return parts.length ? parts.join(': ') : '';
  }

  getSortTitle(pattern) {
    return String(pattern?.title || pattern?.originalTitle || '');
  }

  handleFromTitle(value) {
    return this.normaliseText(value)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  normaliseHandle(value) {
    return this.handleFromTitle(value);
  }

  normaliseText(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ');
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

if (!customElements.get('product-pattern-matches')) {
  customElements.define('product-pattern-matches', ProductPatternMatches);
}
